import axios from 'axios';

// Check if running in Capacitor native platform
const isNativePlatform = () => {
  try {
    // Check if Capacitor is available and we're in a native platform
    return typeof window !== 'undefined' &&
           window.Capacitor &&
           window.Capacitor.isNativePlatform &&
           window.Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

// Get base URL - native apps need full URL, web can use proxy
const getBaseUrl = () => {
  // For native platforms, use environment variable or production URL
  if (isNativePlatform()) {
    // Use VITE_API_URL from build or default to production
    const apiUrl = import.meta.env.VITE_API_URL;
    if (apiUrl) {
      return apiUrl;
    }
    return 'https://api.hisabjourney.com/api';
  }
  // Web app can use proxy in development
  return import.meta.env.VITE_API_URL || '/api';
};

const API_BASE_URL = getBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000 // 30 seconds for large uploads (profile pictures, attachments)
});

// Request interceptor to add auth token and encryption key
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add encryption key header for data encryption/decryption
    const encryptionKey = localStorage.getItem('encryptionKey');
    if (encryptionKey) {
      config.headers['X-Encryption-Key'] = encryptionKey;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Map server error messages to user-friendly messages
const getUserFriendlyErrorMessage = (serverError, status) => {
  const errorMap = {
    'Email already registered': 'This email is already registered. Please use a different email or try logging in.',
    'Account has been deactivated': 'Your account has been deactivated by an administrator. Please contact support@hisabjourney.com to restore access.',
    'Invalid credentials': 'Invalid email or password. Please try again.',
    'User not found': 'No account found with this email address.',
    'Invalid email format': 'Please enter a valid email address.',
    'Password too weak': 'Password must be at least 8 characters with uppercase, lowercase, number, and special character.',
    'Invalid token': 'Your session has expired. Please log in again.',
    'Token expired': 'Your session has expired. Please log in again.',
    'Unauthorized': 'You are not authorized to perform this action.',
    'Server error': 'Something went wrong on our end. Please try again later.',
    'Validation failed': 'Please check your input and try again.',
    'Account not found': 'The account you are looking for does not exist.',
    'Category not found': 'The category you are looking for does not exist.',
    'Transaction not found': 'The transaction you are looking for does not exist.',
    'Contact not found': 'The contact you are looking for does not exist.',
    'Receivable not found': 'The receivable you are looking for does not exist.',
    'Debt not found': 'The debt you are looking for does not exist.',
    'Invalid password': 'The password you entered is incorrect.',
    'Passwords do not match': 'The passwords you entered do not match.',
    'Name already exists': 'An item with this name already exists. Please use a different name.',
    'Cannot delete': 'This item cannot be deleted because it is being used elsewhere.',
    'Subscription required': 'Please subscribe to access this feature.',
    'Trial expired': 'Your trial has expired. Please subscribe to continue.',
  };

  // Check for exact match
  if (errorMap[serverError]) {
    return errorMap[serverError];
  }

  // Check for partial match (case-insensitive)
  const lowerError = serverError.toLowerCase();
  for (const [key, value] of Object.entries(errorMap)) {
    if (lowerError.includes(key.toLowerCase())) {
      return value;
    }
  }

  // Status-based fallbacks
  if (status === 400) {
    return serverError || 'Invalid request. Please check your input and try again.';
  }
  if (status === 401) {
    return serverError || 'Please log in to continue.';
  }
  if (status === 403) {
    return serverError || 'You do not have permission to perform this action.';
  }
  if (status === 404) {
    return serverError || 'The requested item was not found.';
  }
  if (status === 409) {
    return serverError || 'This item already exists or conflicts with another item.';
  }
  if (status === 429) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  if (status >= 500) {
    return 'Something went wrong on our end. Please try again later.';
  }

  // Return the server error if it's user-friendly enough, otherwise a generic message
  if (serverError && serverError.length < 150 && !serverError.includes('Error:') && !serverError.includes('at ')) {
    return serverError;
  }

  return 'Something went wrong. Please try again.';
};

// Helper to format error message from backend
const formatErrorMessage = (errorData, status) => {
  // Handle validation errors array
  if (errorData.errors && Array.isArray(errorData.errors)) {
    const messages = errorData.errors.map(e => e.message || `${e.field} is invalid`);
    return messages.join('. ');
  }

  // Get raw error message
  const rawError = errorData.error || errorData.message || '';

  // Convert to user-friendly message
  return getUserFriendlyErrorMessage(rawError, status);
};

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;

      // Handle 401 Unauthorized - only redirect if token expired (not for login failures)
      if (status === 401) {
        const errorCode = data?.code;
        const deactivated = (data?.error || data?.message || '')
          .toLowerCase()
          .includes('deactivat');
        // Redirect (disconnect) when a logged-in session is invalidated:
        // the token expired, OR the account was deactivated by an admin.
        // Don't redirect for login/register failures (invalid credentials).
        if ((errorCode === 'TOKEN_EXPIRED' || deactivated) && localStorage.getItem('token')) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('encryptionKey');
          if (deactivated) {
            try {
              sessionStorage.setItem('authNotice',
                'Your account has been deactivated by an administrator. Please contact support@hisabjourney.com to restore access.');
            } catch { /* sessionStorage unavailable */ }
          }
          window.dispatchEvent(new Event('auth-expired'));
        }
      }

      // Handle 403 subscription errors - dispatch event for safe navigation (no page reload)
      if (status === 403) {
        const errorCode = data?.code;
        if (['SUBSCRIPTION_REQUIRED', 'SUBSCRIPTION_ENDED', 'TRIAL_EXPIRED'].includes(errorCode)) {
          window.dispatchEvent(new CustomEvent('subscription-expired', { detail: { code: errorCode } }));
        }
      }

      // Format and return error with detailed message
      const formattedError = {
        error: formatErrorMessage(data, status),
        status,
        errors: data.errors || null,
        raw: data
      };

      return Promise.reject(formattedError);
    }

    if (error.request) {
      // Request was made but no response received
      return Promise.reject({
        error: 'Unable to connect. Please check your internet connection and try again.',
        status: 0
      });
    }

    // Something else happened
    return Promise.reject({
      error: 'Something went wrong. Please try again.',
      status: -1
    });
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  googleAuth: (payload) => api.post('/auth/google', payload),
  appleAuth: (payload) => api.post('/auth/apple', payload),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/password', data),
  setPassword: (data) => api.post('/auth/set-password', data),
  skipPasswordSetup: () => api.post('/auth/skip-password-setup'),
  logout: () => api.post('/auth/logout'),
  // Encryption key management
  generateEncryptionKey: () => api.get('/auth/generate-encryption-key'),
  setEncryptionKey: (encryptionKey) => api.post('/auth/encryption-key', { encryptionKey }),
  viewEncryptionKey: (password) => api.post('/auth/view-encryption-key', { password }),
  getEncryptionKeyStatus: () => api.get('/auth/encryption-key-status'),
  // Account deletion
  deleteAccount: (password) => api.delete('/auth/account', { data: { password } }),
  // Password verification (for passcode reset)
  verifyPassword: (data) => api.post('/auth/verify-password', data)
};

// Transaction API
export const transactionAPI = {
  getAll: (params) => api.get('/transactions', { params }),
  getById: (id) => api.get(`/transactions/${id}`),
  create: (data) => api.post('/transactions', data),
  update: (id, data) => api.put(`/transactions/${id}`, data),
  delete: (id) => api.delete(`/transactions/${id}`),
  bulkDelete: (ids) => api.post('/transactions/bulk-delete', { ids }),
  bulkCreate: (transactions) => api.post('/transactions/bulk-create', { transactions }),
  getSummary: (params) => api.get('/transactions/summary', { params }),
  getCategoryBreakdown: (params) => api.get('/transactions/category-breakdown', { params }),
  getTaxData: () => api.get('/transactions/tax-data'),
  getMonthlyTrend: (params) => api.get('/transactions/monthly-trend', { params }),
  getFinancialYears: () => api.get('/transactions/financial-years'),
  getCalendarData: (year, month) => api.get('/transactions/calendar', { params: { year, month } }),
  getStats: (params) => api.get('/transactions/stats', { params }),
  getLedger: (params) => api.get('/transactions/ledger', { params }),
  getOpeningBalance: (params) => api.get('/transactions/opening-balance', { params }),
  getDescriptions: () => api.get('/transactions/descriptions')
};

// Category API
export const categoryAPI = {
  getAll: (params) => api.get('/categories', { params }),
  getById: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  initDefaults: () => api.post('/categories/init-defaults'),
  reorder: (categoryIds, type) => api.put('/categories/reorder', { categoryIds, type })
};

// Account API
export const accountAPI = {
  getAll: (params) => api.get('/accounts', { params }),
  getWithBalances: () => api.get('/accounts/with-balances'),
  getById: (id) => api.get(`/accounts/${id}`),
  create: (data) => api.post('/accounts', data),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  delete: (id, permanent = false) => api.delete(`/accounts/${id}`, { params: { permanent } }),
  resetBalance: (id, newBalance) => api.put(`/accounts/${id}/reset-balance`, { newBalance }),
  checkDeleted: (name) => api.get(`/accounts/check-deleted/${encodeURIComponent(name)}`)
};

// Feedback API
export const feedbackAPI = {
  create: (data) => api.post('/feedback', data)
};

// Admin API
export const adminAPI = {
  // Dashboard stats
  getStats: () => api.get('/admin/stats'),
  // User management
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserDetail: (id) => api.get(`/admin/users/${id}`),
  updateUserStatus: (id, isActive) => api.put(`/admin/users/${id}/status`, { isActive }),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  // Lead management
  getLeads: (params) => api.get('/admin/leads', { params }),
  updateLead: (id, data) => api.put(`/admin/leads/${id}`, data),
  replyToLead: (id, subject, message) => api.post(`/admin/leads/${id}/reply`, { subject, message }),
  // Feedback management
  getFeedback: (params) => api.get('/admin/feedback', { params }),
  updateFeedback: (id, data) => api.put(`/admin/feedback/${id}`, data),
  replyToFeedback: (id, subject, message) => api.post(`/admin/feedback/${id}/reply`, { subject, message }),
  // Report management
  getReports: (params) => api.get('/reports', { params }),
  getReportById: (id) => api.get(`/reports/${id}`),
  updateReport: (id, data) => api.put(`/reports/${id}`, data),
  getReportStats: () => api.get('/reports/stats'),
  toggleEmailBlock: (id, blocked) => api.post(`/reports/${id}/block-emails`, { blocked }),
  // Received emails management
  getReceivedEmails: (params) => api.get('/admin/received-emails', { params }),
  getReceivedEmailById: (id) => api.get(`/admin/received-emails/${id}`),
  updateReceivedEmail: (id, data) => api.put(`/admin/received-emails/${id}`, data),
  deleteReceivedEmail: (id) => api.delete(`/admin/received-emails/${id}`),
  replyToReceivedEmail: (id, subject, message) => api.post(`/admin/received-emails/${id}/reply`, { subject, message }),
  // FCM token management
  deleteUserFcmToken: (userId, deviceId) => api.delete(`/admin/users/${userId}/fcm-token/${deviceId}`)
};

// Recurring Transaction API
export const recurringTransactionAPI = {
  getAll: (params) => api.get('/recurring-transactions', { params }),
  getById: (id) => api.get(`/recurring-transactions/${id}`),
  create: (data) => api.post('/recurring-transactions', data),
  update: (id, data) => api.put(`/recurring-transactions/${id}`, data),
  delete: (id) => api.delete(`/recurring-transactions/${id}`),
  toggle: (id) => api.patch(`/recurring-transactions/${id}/toggle`)
};

// Insights API
export const insightsAPI = {
  getAll: (params) => api.get('/insights', { params }),
  getSummary: () => api.get('/insights/summary')
};

// Contact API
export const contactAPI = {
  getAll: (params) => api.get('/contacts', { params }),
  getById: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
  search: (q) => api.get('/contacts/search', { params: { q } }),
  getHistory: (id) => api.get(`/contacts/${id}/history`)
};

// Receivable API
export const receivableAPI = {
  getAll: (params) => api.get('/receivables', { params }),
  getById: (id) => api.get(`/receivables/${id}`),
  create: (data) => api.post('/receivables', data),
  update: (id, data) => api.put(`/receivables/${id}`, data),
  cancel: (id) => api.delete(`/receivables/${id}`),
  deletePermanent: (id) => api.delete(`/receivables/${id}/permanent`),
  getSummary: () => api.get('/receivables/summary'),
  recordPayment: (id, data) => api.post(`/receivables/${id}/payments`, data),
  settle: (id, data) => api.post(`/receivables/${id}/settle`, data),
  sendReminder: (id) => api.post(`/receivables/${id}/send-reminder`)
};

// Debt API
export const debtAPI = {
  getAll: (params) => api.get('/debts', { params }),
  getById: (id) => api.get(`/debts/${id}`),
  create: (data) => api.post('/debts', data),
  update: (id, data) => api.put(`/debts/${id}`, data),
  cancel: (id) => api.delete(`/debts/${id}`),
  deletePermanent: (id) => api.delete(`/debts/${id}/permanent`),
  getSummary: () => api.get('/debts/summary'),
  recordPayment: (id, data) => api.post(`/debts/${id}/payments`, data),
  settle: (id, data) => api.post(`/debts/${id}/settle`, data),
  sendReminder: (id) => api.post(`/debts/${id}/send-reminder`)
};

// Payment API
export const paymentAPI = {
  getAll: (params) => api.get('/payments', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`)
};

// Upload API - for direct file uploads
export const uploadAPI = {
  /**
   * Upload profile picture directly to Cloudinary
   * @param {File} file - The file to upload
   * @returns {Promise<{url: string, publicId: string}>}
   */
  profilePicture: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/upload/profile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data.data;
  },

  /**
   * Upload transaction attachment directly to Cloudinary
   * @param {File} file - The file to upload
   * @param {string} transactionId - Optional transaction ID for naming
   * @returns {Promise<{url: string, publicId: string, filename: string, mimeType: string}>}
   */
  attachment: async (file, transactionId = 'temp') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('transactionId', transactionId);

    const response = await api.post('/upload/attachment', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data.data;
  }
};

/**
 * Fetch media from proxy endpoint as blob and create object URL
 * Used for private images that require authentication
 * @param {string} path - API path (e.g., '/api/media/profile/123')
 * @param {string} expectedMimeType - Optional MIME type to ensure correct blob type
 * @returns {Promise<string>} Blob URL for the media
 */
// Subscription API
export const subscriptionAPI = {
  // Get plan details (public) - supports ?source=ios for iOS pricing
  getPlans: (source) => api.get('/subscription/plans', { params: source ? { source } : {} }),

  // Get current subscription status
  getSubscription: () => api.get('/subscription'),

  // Start trial
  startTrial: () => api.post('/subscription/start-trial'),

  // Apply coupon code
  applyCoupon: (code, currency, billingInterval) => api.post('/subscription/apply-coupon', { code, ...(currency && { currency }), ...(billingInterval && { billingInterval }) }),

  // Create checkout session
  checkout: (couponCode, billingInterval = 'monthly', source, currency) =>
    api.post('/subscription/checkout', { couponCode, billingInterval, ...(source && { source }), ...(currency && { currency }) }),

  // Verify payment
  verifyPayment: (data) => api.post('/subscription/verify', data),

  // Apple IAP
  verifyApplePurchase: (data) => api.post('/subscription/apple/verify', data),
  restoreApplePurchase: (data) => api.post('/subscription/apple/restore', data),

  // Switch plan (scheduled at end of current period)
  switchPlan: (billingInterval) => api.post('/subscription/switch-plan', { billingInterval }),

  // Cancel subscription
  cancel: (reason) => api.post('/subscription/cancel', { reason }),

  // Pause subscription
  pause: () => api.post('/subscription/pause'),

  // Resume subscription
  resume: () => api.post('/subscription/resume'),

  // Get payment history
  getInvoices: (params) => api.get('/subscription/invoices', { params })
};

// Admin Coupon API
export const adminCouponAPI = {
  // Get all coupons
  getCoupons: (params) => api.get('/admin/coupons', { params }),

  // Generate unique coupon code
  generateCode: () => api.get('/admin/coupons/generate-code'),

  // Get coupon by ID
  getCouponById: (id) => api.get(`/admin/coupons/${id}`),

  // Create coupon
  createCoupon: (data) => api.post('/admin/coupons', data),

  // Update coupon
  updateCoupon: (id, data) => api.put(`/admin/coupons/${id}`, data),

  // Delete coupon
  deleteCoupon: (id) => api.delete(`/admin/coupons/${id}`),

  // Get coupon usage history
  getCouponUsage: (id, params) => api.get(`/admin/coupons/${id}/usage`, { params })
};

// Admin Plan Config API
export const adminPlanAPI = {
  // Get plan configuration
  getConfig: () => api.get('/admin/plan-config'),

  // Update plan configuration
  updateConfig: (data) => api.put('/admin/plan-config', data),

  // Create Razorpay plans for all intervals
  createRazorpayPlans: () => api.post('/admin/plan-config/create-razorpay-plans')
};

// Admin Subscriptions API
export const adminSubscriptionsAPI = {
  // Get all subscriptions
  getSubscriptions: (params) => api.get('/admin/subscriptions', { params }),

  // Get subscription stats
  getStats: () => api.get('/admin/subscription-stats'),

  // Get subscription by user ID
  getByUserId: (userId) => api.get(`/admin/subscriptions/${userId}`),

  // Extend subscription
  extendSubscription: (userId, days, reason) =>
    api.post(`/admin/subscriptions/${userId}/extend`, { days, reason }),

  // Reactivate cancelled subscription
  reactivateSubscription: (userId, days, reason) =>
    api.post(`/admin/subscriptions/${userId}/reactivate`, { days, reason }),

  // Cancel user subscription
  cancelSubscription: (userId, immediate, reason) =>
    api.post(`/admin/subscriptions/${userId}/cancel`, { immediate, reason }),

  // Send payment link email to user
  sendPaymentLink: (userId) =>
    api.post(`/admin/subscriptions/${userId}/send-payment-link`),

  // Get Razorpay invoices for a user
  getInvoices: (userId) =>
    api.get(`/admin/subscriptions/${userId}/invoices`),

  // Payment report
  getPaymentReport: (params) => api.get('/admin/payment-report', { params }),

  // Settlements
  getSettlements: (params) => api.get('/admin/settlements', { params }),
  syncSettlements: () => api.post('/admin/settlements/sync')
};

// Notification API
export const notificationAPI = {
  // Get all notifications for current user
  getAll: (params) => api.get('/notifications', { params }),

  // Get single notification
  getById: (id) => api.get(`/notifications/${id}`),

  // Get unread count
  getUnreadCount: () => api.get('/notifications/unread-count'),

  // Mark as read
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),

  // Mark all as read
  markAllAsRead: () => api.patch('/notifications/mark-all-read'),

  // Delete notification
  delete: (id) => api.delete(`/notifications/${id}`),

  // Clear all notifications (soft delete all non-broadcast)
  clearAll: () => api.delete('/notifications/clear-all'),

  // User notification preferences
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data) => api.put('/notifications/preferences', data),

  // Phone verification
  sendPhoneVerification: (phoneNumber) => api.post('/notifications/verify-phone', { phoneNumber }),
  confirmPhoneVerification: (phoneNumber, code) => api.post('/notifications/confirm-phone', { phoneNumber, code }),
  removePhone: () => api.delete('/notifications/phone'),

  // SMS quota
  getSmsQuota: () => api.get('/notifications/sms-quota'),

  // Device token (push notifications)
  registerDeviceToken: (data) => api.post('/notifications/device-token', data),
  unregisterDeviceToken: (deviceId) => api.delete(`/notifications/device-token/${deviceId}`)
};

// Admin Notification API
export const adminNotificationAPI = {
  // Broadcast notification to all users
  broadcast: (data) => api.post('/notifications/broadcast', data),

  // Get all notifications (admin view)
  getAll: (params) => api.get('/notifications/admin/all', { params }),

  // Delete notification (admin)
  delete: (id) => api.delete(`/notifications/admin/${id}`)
};

// Admin Notification Channels API
export const adminNotificationChannelsAPI = {
  // Get notification channel configuration
  getConfig: () => api.get('/admin/notification-channels'),

  // Update notification channel configuration
  updateConfig: (data) => api.put('/admin/notification-channels', data)
};

// Admin Blog API
export const adminBlogAPI = {
  // List blogs (incl. drafts), with optional { page, limit, status, category, search }
  getAll: (params) => api.get('/admin/blog', { params }),

  // Get one blog by id (full content)
  getById: (id) => api.get(`/admin/blog/${id}`),

  // Create a blog
  create: (data) => api.post('/admin/blog', data),

  // Update a blog
  update: (id, data) => api.put(`/admin/blog/${id}`, data),

  // Delete a blog
  delete: (id) => api.delete(`/admin/blog/${id}`),

  // Upload an image (base64 data URL) -> { url, publicId }
  uploadImage: (image) => api.post('/admin/blog/upload-image', { image })
};

/**
 * Fetch media from proxy endpoint as blob and create object URL
 * Used for private images that require authentication
 * @param {string} path - API path (e.g., '/api/media/profile/123')
 * @param {string} expectedMimeType - Optional MIME type to ensure correct blob type
 * @returns {Promise<string>} Blob URL for the media
 */
export const fetchMediaAsBlob = async (path, expectedMimeType = null) => {
  // Remove /api prefix if present since baseURL already includes it
  const apiPath = path.startsWith('/api') ? path.substring(4) : path;

  // Ensure token exists before making request
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token available');
  }

  const response = await api.get(apiPath, {
    responseType: 'blob'
  });

  // If we have an expected MIME type and the blob type doesn't match, recreate with correct type
  // This helps browsers properly handle the file type
  let blob = response.data;
  if (expectedMimeType && blob.type !== expectedMimeType) {
    blob = new Blob([blob], { type: expectedMimeType });
  }

  return URL.createObjectURL(blob);
};

// ============================================
// Referral API
// ============================================
export const referralAPI = {
  getMyCode: () => api.get('/referrals/my-code'),
  getDashboard: () => api.get('/referrals/dashboard'),
  validateCode: (code) => api.post(`/referrals/validate/${code}`)
};

export const adminReferralAPI = {
  getConfig: () => api.get('/admin/referral-config'),
  updateConfig: (data) => api.put('/admin/referral-config', data),
  getUserReferrals: (id) => api.get(`/admin/users/${id}/referrals`),
  setOverrides: (id, data) => api.put(`/admin/users/${id}/referral-overrides`, data),
  setDiscount: (id, data) => api.put(`/admin/users/${id}/referral-discount`, data)
};

// Admin AI Config API
export const adminAiConfigAPI = {
  getConfig: () => api.get('/admin/ai-config'),
  updateConfig: (data) => api.put('/admin/ai-config', data),
};

export const checklistAPI = {
  getAll: () => api.get('/checklist'),
  create: (data) => api.post('/checklist', data),
  update: (id, data) => api.put(`/checklist/${id}`, data),
  delete: (id) => api.delete(`/checklist/${id}`),
  toggle: (id, month) => api.patch(`/checklist/${id}/toggle`, { month }),
};

export default api;
